/**
 * Récupération après déploiement.
 *
 * Les fichiers de l'application portent un hash de contenu dans leur nom
 * (`CarriersPage-C-5SaWU8.js`). À chaque déploiement ces noms changent et les
 * anciens disparaissent du serveur. Un onglet resté ouvert, lui, garde en
 * mémoire l'`index.html` d'avant : au premier changement d'écran, il réclame
 * un morceau qui n'existe plus, l'import dynamique échoue, et l'utilisateur
 * tombe sur « Failed to fetch dynamically imported module ».
 *
 * Ce n'est pas un bug de l'écran demandé : c'est un onglet périmé. La seule
 * réparation possible est de recharger — autant le faire à la place de
 * l'utilisateur plutôt que de lui montrer une page morte à chaque livraison.
 *
 * Le garde-fou compte autant que le rechargement : si le morceau manque pour
 * une AUTRE raison (réseau coupé, CDN en vrac, fichier réellement absent),
 * recharger en boucle transformerait une page cassée en onglet inutilisable.
 * On ne retente donc qu'une fois par fenêtre de temps ; au-delà, l'erreur
 * s'affiche, ce qui est le comportement honnête.
 */

/** Horodatage de la dernière tentative — par onglet, pas par navigateur. */
const RELOAD_KEY = 'cb:chunk-reload-at';

/** En deçà, un second échec n'est plus « un déploiement vient d'avoir lieu ». */
export const RELOAD_GUARD_MS = 30_000;

/**
 * Les navigateurs ne s'accordent pas sur le message. Chrome parle de « fetch
 * dynamically imported module », Firefox de « error loading dynamically
 * imported module », Safari d'« importing a module script failed » — et Vite
 * ajoute le préchargement CSS. On reconnaît la famille, pas une phrase.
 */
const CHUNK_ERROR_PATTERNS = [
  'dynamically imported module',
  'importing a module script failed',
  'error loading dynamically imported module',
  'failed to fetch dynamically imported',
  'unable to preload css',
  'loading chunk',
];

export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  const haystack = message.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => haystack.includes(pattern));
}

/** Lecture/écriture tolérantes : `sessionStorage` jette en navigation privée. */
function lastAttempt(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
  } catch {
    return 0;
  }
}

function markAttempt(now: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    /* navigation privée : on rechargera au plus une fois, sans mémoire. */
  }
}

/**
 * Recharge une fois. Renvoie `false` si le garde-fou a joué — l'appelant
 * laisse alors l'erreur remonter jusqu'à l'écran.
 */
export function reloadOnce(now = Date.now()): boolean {
  if (now - lastAttempt() < RELOAD_GUARD_MS) return false;
  markAttempt(now);
  window.location.reload();
  return true;
}

/**
 * À brancher une fois au démarrage, avant le rendu.
 *
 * Deux sources, parce qu'aucune ne couvre tout : `vite:preloadError` est émis
 * par le chargeur de Vite au moment précis de l'échec, et le rejet non
 * intercepté rattrape les imports déclenchés hors de ce chemin.
 */
export function installChunkRecovery(): void {
  window.addEventListener('vite:preloadError', (event) => {
    // Sans ceci, Vite relance l'erreur et l'écran casse avant le rechargement.
    event.preventDefault();
    reloadOnce();
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) reloadOnce();
  });
}
